import { type ReactElement, useEffect, useRef, useState } from "react";

export interface ProgressBarSegmentData {
  label: string;
}

interface ProgressBarProps {
  segments: ProgressBarSegmentData[];
  currentPage: number;
}

type SegmentState = "completed" | "active" | "future";
type TransitionRole = "leaving" | "entering" | "static";
type TurnstilePhase = "idle" | "setup" | "animating";

export function ProgressBar({ segments, currentPage }: ProgressBarProps) {
  const clampedPage = Math.min(currentPage, segments.length);
  const allComplete = currentPage > segments.length;
  const activeLabel = !allComplete ? segments[clampedPage - 1]?.label : "";
  const countText = `${clampedPage} of ${segments.length}`;

  const prevPageRef = useRef(clampedPage);
  const prevPage = prevPageRef.current;

  useEffect(() => {
    prevPageRef.current = clampedPage;
  });

  // Turnstile animation for label row
  const [turnstile, setTurnstile] = useState<{
    phase: TurnstilePhase;
    oldLabel: string;
    oldCount: string;
  }>({ phase: "idle", oldLabel: "", oldCount: "" });

  const prevLabelRef = useRef(activeLabel);
  const prevCountRef = useRef(countText);

  useEffect(() => {
    const prevLabel = prevLabelRef.current;
    const prevCount = prevCountRef.current;
    if (prevLabel !== activeLabel || prevCount !== countText) {
      prevLabelRef.current = activeLabel;
      prevCountRef.current = countText;

      // Only trigger the flip when there was a visible label leaving.
      // No-label → label is handled by the height expansion revealing content.
      if (prevLabel) {
        setTurnstile({
          phase: "setup",
          oldLabel: prevLabel,
          oldCount: prevCount,
        });

        const raf = requestAnimationFrame(() => {
          setTurnstile({
            phase: "animating",
            oldLabel: prevLabel,
            oldCount: prevCount,
          });
        });

        const timer = setTimeout(() => {
          setTurnstile({ phase: "idle", oldLabel: "", oldCount: "" });
        }, 500);

        return () => {
          cancelAnimationFrame(raf);
          clearTimeout(timer);
        };
      }
    }
  }, [activeLabel, countText]);

  // Height animation: smoothly expand/collapse the label row when label presence toggles
  const hadLabelRef = useRef(!!activeLabel);
  const [wrapperHeight, setWrapperHeight] = useState(
    !!activeLabel ? "20px" : "0px",
  );
  const [wrapperMargin, setWrapperMargin] = useState(
    !!activeLabel ? "8px" : "0px",
  );
  const [heightTransition, setHeightTransition] = useState("none");

  useEffect(() => {
    const nowHasLabel = !!activeLabel;
    const hadLabel = hadLabelRef.current;
    if (hadLabel === nowHasLabel) return;
    hadLabelRef.current = nowHasLabel;

    if (!nowHasLabel) {
      // Collapse: wait for turnstile flip-out to clear (200ms), then slide height down
      const timer = setTimeout(() => {
        setHeightTransition(
          "height 250ms ease-in-out, margin-bottom 250ms ease-in-out",
        );
        setWrapperHeight("0px");
        setWrapperMargin("0px");
      }, 200);
      return () => clearTimeout(timer);
    } else {
      // Expand: start immediately — content is clipped and revealed as height grows
      setHeightTransition(
        "height 300ms cubic-bezier(0.0, 0, 0.2, 1), margin-bottom 300ms cubic-bezier(0.0, 0, 0.2, 1)",
      );
      setWrapperHeight("20px");
      setWrapperMargin("8px");
      const timer = setTimeout(() => setHeightTransition("none"), 350);
      return () => clearTimeout(timer);
    }
  }, [activeLabel]);

  return (
    <div
      className="progress-bar"
      style={{
        width: "min(100vw, var(--block-width))",
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        padding: "12px 24px",
        borderBottom: "1px solid #d4d4d4",
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}
      role="progressbar"
      aria-valuenow={clampedPage}
      aria-valuemin={1}
      aria-valuemax={segments.length}
      aria-label="Review progress"
    >
      <span className="sr-only">
        Step {clampedPage} of {segments.length}
        {activeLabel ? `: ${activeLabel}` : ""}
      </span>
      {/* Label row — always rendered, height animated to avoid abrupt jumps */}
      <div
        style={{
          height: wrapperHeight,
          marginBottom: wrapperMargin,
          overflow: "hidden",
          transition: heightTransition,
        }}
        aria-hidden="true"
      >
        <div
          style={{
            position: "relative",
            height: "20px",
            overflow: "hidden",
            perspective: "300px",
          }}
        >
          {/* Old label — flips out */}
          {turnstile.phase !== "idle" && turnstile.oldLabel && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                transform:
                  turnstile.phase === "setup"
                    ? "rotateX(0deg)"
                    : "rotateX(90deg)",
                transition:
                  turnstile.phase === "animating"
                    ? "transform 200ms ease-in"
                    : "none",
                transformOrigin: "bottom center",
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#1a1a1a",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {turnstile.oldLabel}
              </span>
              <span
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  whiteSpace: "nowrap",
                  marginLeft: "12px",
                  flexShrink: 0,
                }}
              >
                {turnstile.oldCount}
              </span>
            </div>
          )}
          {/* New label — flips in */}
          {activeLabel && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                transform:
                  turnstile.phase === "setup"
                    ? "rotateX(-90deg)"
                    : "rotateX(0deg)",
                transition:
                  turnstile.phase === "animating"
                    ? "transform 200ms ease-out 150ms"
                    : "none",
                transformOrigin: "top center",
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#1a1a1a",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {activeLabel}
              </span>
              <span
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  whiteSpace: "nowrap",
                  marginLeft: "12px",
                  flexShrink: 0,
                }}
              >
                {countText}
              </span>
            </div>
          )}
        </div>
      </div>
      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
        {segments.map((_, i) => {
          const pageNum = i + 1;
          const state: SegmentState =
            allComplete || pageNum < clampedPage
              ? "completed"
              : pageNum === clampedPage
                ? "active"
                : "future";

          let transitionRole: TransitionRole = "static";
          if (prevPage !== clampedPage) {
            if (pageNum === prevPage) transitionRole = "leaving";
            else if (pageNum === clampedPage) transitionRole = "entering";
          }

          return (
            <ProgressBarSegment
              key={i}
              state={state}
              transitionRole={transitionRole}
            />
          );
        })}
      </div>
    </div>
  );
}

function ProgressBarSegment({
  state,
  transitionRole,
}: {
  state: SegmentState;
  transitionRole: TransitionRole;
}): ReactElement {
  const fillPercentage =
    state === "completed" ? 100 : state === "active" ? 33 : 0;

  // Choreography:
  // 1. Leaving bar fills 33%→100%:  200ms, snappy — completes quickly
  // 2. Pause:                        ~350ms — let the full bar breathe
  // 3. Both bars resize:            400ms, bouncy, starts at 550ms
  // 4. Entering bar fills 0%→33%:   500ms, subtle bounce, starts at 900ms
  const bounce = "cubic-bezier(0.34, 1.56, 0.64, 1)";
  const subtleBounce = "cubic-bezier(0.25, 1.35, 0.5, 1)";

  const outerTransition =
    transitionRole === "leaving"
      ? `flex 400ms ${bounce} 550ms`
      : transitionRole === "entering"
        ? `flex 400ms ${bounce} 550ms`
        : `flex 400ms ${bounce}`;

  const innerTransition =
    transitionRole === "leaving"
      ? "width 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms"
      : transitionRole === "entering"
        ? `width 500ms ${subtleBounce} 900ms`
        : "width 350ms cubic-bezier(0.4, 0, 0.2, 1)";

  return (
    <div
      aria-hidden="true"
      style={{
        flex: state === "active" ? 2 : 1,
        minWidth: "12px",
        height: "6px",
        backgroundColor: "#e5e5e5",
        borderRadius: "9999px",
        overflow: "hidden",
        transition: outerTransition,
      }}
    >
      <div
        style={{
          width: `${fillPercentage}%`,
          height: "100%",
          backgroundColor: "#1a1a1a",
          borderRadius: "9999px",
          transition: innerTransition,
        }}
      />
    </div>
  );
}
